// @ts-check
import axios from 'axios'

const BASE = '/api/tenant-phone-numbers'

/** @typedef {import('./schema').components['schemas']} Schemas */
/** @typedef {Schemas['TenantPhoneNumber']} TenantPhoneNumber */

/**
 * GET /api/tenant-phone-numbers/ — all phone numbers for the current tenant (admin).
 * @returns {Promise<TenantPhoneNumber[]>}
 */
export async function listTenantPhoneNumbers() {
  const { data } = await axios.get(`${BASE}/`)
  return data
}

/**
 * GET /api/tenant-phone-numbers/available/ — active, voice-enabled numbers for the current tenant.
 * @returns {Promise<TenantPhoneNumber[]>}
 */
export async function listAvailableNumbers() {
  const { data } = await axios.get(`${BASE}/available/`)
  return data
}

/**
 * GET /api/tenant-phone-numbers/{id}/phone-sessions/ — paginated phone sessions for a phone number.
 * @param {number} id
 * @param {Record<string, string | number>} [params]
 * @returns {Promise<{count: number, next: string|null, previous: string|null, results: import('./schema').components['schemas']['TelehealthPhoneSession'][]}>}
 */
export async function listPhoneNumberPhoneSessions(id, params) {
  const { data } = await axios.get(`${BASE}/${id}/phone-sessions/`, { params })
  return data
}

/**
 * GET /api/tenant-phone-numbers/{id}/ — single phone number detail.
 * @param {number} id
 * @returns {Promise<TenantPhoneNumber>}
 */
export async function getPhoneNumber(id) {
  const { data } = await axios.get(`${BASE}/${id}/`)
  return data
}

/**
 * POST /api/tenant-phone-numbers/{id}/upload-greeting/ — upload voicemail greeting audio.
 * @param {number} id
 * @param {File} file
 * @returns {Promise<TenantPhoneNumber>}
 */
export async function uploadGreeting(id, file) {
  const form = new FormData()
  form.append('file', file)
  const { data } = await axios.post(`${BASE}/${id}/upload-greeting/`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

/**
 * POST /api/tenant-phone-numbers/{id}/delete-greeting/ — remove voicemail greeting.
 * @param {number} id
 * @returns {Promise<TenantPhoneNumber>}
 */
export async function deleteGreeting(id) {
  const { data } = await axios.post(`${BASE}/${id}/delete-greeting/`)
  return data
}

/**
 * POST /api/tenant-phone-numbers/{id}/assign-user/ — assign or unassign a user.
 * @param {number} id
 * @param {number|null} userId
 * @returns {Promise<TenantPhoneNumber>}
 */
export async function assignUser(id, userId) {
  const { data } = await axios.post(`${BASE}/${id}/assign-user/`, {
    user_id: userId,
  })
  return data
}

/** @typedef {Schemas['AvailablePhoneNumber']} AvailablePhoneNumber */
/** @typedef {Schemas['TelephonyConfig']} TelephonyConfig */
/** @typedef {Schemas['TelephonyConfigResponse']} TelephonyConfigResponse */
/** @typedef {Schemas['TelephonyError']} TelephonyError */
/** @typedef {Schemas['TenantPhoneNumberPurchase']} TenantPhoneNumberPurchase */

/**
 * GET /api/tenant-phone-numbers/admin-list/ — every number for the tenant, any status (admin view).
 * @returns {Promise<TenantPhoneNumber[]>}
 */
export async function listAdminPhoneNumbers() {
  const { data } = await axios.get(`${BASE}/admin-list/`)
  return data
}

/**
 * GET /api/tenant-phone-numbers/telephony-config/ — approved area codes and whether purchasing is enabled here.
 * @returns {Promise<TelephonyConfigResponse>}
 */
export async function getTelephonyConfig() {
  const { data } = await axios.get(`${BASE}/telephony-config/`)
  return data
}

/**
 * POST /api/tenant-phone-numbers/telephony-config/ — replace the approved area codes (tenant admins and superusers).
 * @param {TelephonyConfig} body
 * @returns {Promise<TelephonyConfigResponse>}
 */
export async function updateTelephonyConfig(body) {
  const { data } = await axios.post(`${BASE}/telephony-config/`, body)
  return data
}

/**
 * GET /api/tenant-phone-numbers/search-available/ — Twilio numbers in an approved area code (tenant admins and superusers).
 * @param {string} areaCode
 * @returns {Promise<AvailablePhoneNumber[]>}
 */
export async function searchAvailablePhoneNumbers(areaCode) {
  const { data } = await axios.get(`${BASE}/search-available/`, { params: { area_code: areaCode } })
  return data
}

/**
 * POST /api/tenant-phone-numbers/purchase/ — buy a number and assign it (tenant admins and superusers, production only).
 * @param {TenantPhoneNumberPurchase} body
 * @returns {Promise<TenantPhoneNumber>}
 */
export async function purchasePhoneNumber(body) {
  const { data } = await axios.post(`${BASE}/purchase/`, body)
  return data
}

/**
 * The typed error body from the telephony admin endpoints, or null for other failures.
 * @param {unknown} err - an axios error
 * @returns {TelephonyError | null}
 */
export function telephonyError(err) {
  const data = err?.response?.data
  return data && typeof data.code === 'string' && typeof data.message === 'string' ? data : null
}
